import { useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Trash2, ZoomIn } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from '@/components/ui/toaster';
import { useAuth } from '@/features/auth/AuthProvider';
import {
  fetchBillImagesPage,
  uploadBillImage,
  deleteBillImage,
  type BillImage,
  type BillImagesPage,
} from './billQueries';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

export function HandmadeBillsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<BillImage | null>(null);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<BillImagesPage, Error, { pages: BillImagesPage[] }, string[], QueryDocumentSnapshot<DocumentData> | null>({
    queryKey: ['handmade-bills'],
    queryFn: ({ pageParam }) => fetchBillImagesPage(pageParam),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.cursor,
  });

  const bills = useMemo(() => data?.pages.flatMap((p) => p.bills) ?? [], [data]);

  const upload = useMutation({
    mutationFn: (file: File) => uploadBillImage(file, user?.uid ?? null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['handmade-bills'] });
      toast({ title: 'Bill saved', variant: 'success' });
    },
    onError: (e) =>
      toast({ title: 'Upload failed', description: String(e), variant: 'destructive' }),
  });

  const remove = useMutation({
    mutationFn: deleteBillImage,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['handmade-bills'] });
      toast({ title: 'Bill deleted', variant: 'success' });
    },
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    upload.mutate(file);
    // reset so same file can be re-selected
    e.target.value = '';
  }

  return (
    <div>
      <PageHeader
        title="Handmade Bills"
        subtitle="Capture and store rough bills serially."
        actions={
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileChange}
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={upload.isPending}
            >
              {upload.isPending ? (
                'Uploading…'
              ) : (
                <>
                  <Camera className="h-4 w-4" />
                  <span className="hidden sm:inline">Capture / Upload</span>
                </>
              )}
            </Button>
          </>
        }
      />

      {isLoading && (
        <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
      )}

      {!isLoading && bills.length === 0 && (
        <div className="flex flex-col items-center gap-4 p-16 text-center">
          <Camera className="h-12 w-12 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            No bills yet. Tap the button above to capture your first one.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {bills.map((bill) => (
          <Card key={bill.id} className="group relative overflow-hidden p-0">
            <img
              src={bill.url}
              alt={`Bill #${bill.serial}`}
              className="aspect-[3/4] w-full object-cover"
              loading="lazy"
            />
            {/* Serial badge */}
            <div className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
              #{bill.serial}
            </div>
            {/* Hover actions */}
            <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
              <Button
                size="icon"
                variant="secondary"
                className="h-8 w-8"
                onClick={() => setPreview(bill)}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="destructive"
                className="h-8 w-8"
                onClick={() => {
                  if (confirm(`Delete bill #${bill.serial}?`)) remove.mutate(bill.id);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}

      {/* Lightbox */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPreview(null)}
        >
          <img
            src={preview.url}
            alt={`Bill #${preview.serial}`}
            className="max-h-full max-w-full rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute right-4 top-4 text-white/80 hover:text-white text-2xl leading-none"
            onClick={() => setPreview(null)}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
